import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ACMeetingCard } from '../components/ACMeetingCard';
import { ACBrokeredPrepCard } from '../components/ACBrokeredPrepCard';
import { FileText, Trash2, ArrowLeft, Copy, Check, ChevronRight, Ban, Landmark } from 'lucide-react';

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');

export default function ACMeetingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showAgendaEmails, setShowAgendaEmails] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadHistory = useCallback(async () => {
    try { const res = await api.getMeetingHistory(); setHistory(res.data); } catch {}
  }, []);
  const loadStats = useCallback(async () => {
    try { const res = await api.getAcStats(); setStats(res.data); } catch {}
  }, []);
  useEffect(() => { loadHistory(); loadStats(); }, [loadHistory, loadStats]);

  const summaries = history.filter(r => r.report_type === 'ac_meeting_summary');
  const agendaEmails = history.filter(r => r.report_type === 'ac_meeting');
  const passed = stats?.passed_since_last_ac || [];

  const viewReport = async (id) => {
    try { const res = await api.getMeetingReport(id); setActiveReport(res.data); window.scrollTo(0, 0); }
    catch { toast.error('Report konnte nicht geladen werden'); }
  };
  const deleteReport = async (id) => {
    try { await api.deleteMeetingReport(id); toast.success('Gelöscht'); loadHistory(); if (activeReport?.id === id) setActiveReport(null); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };
  const copyReport = async () => {
    if (!activeReport) return;
    try {
      const blob = new Blob([activeReport.body], { type: 'text/html' });
      const plain = new Blob([activeReport.body.replace(/<[^>]+>/g, '')], { type: 'text/plain' });
      await navigator.clipboard.write([new window.ClipboardItem({ 'text/html': blob, 'text/plain': plain })]);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Kopieren fehlgeschlagen'); }
  };

  if (activeReport) {
    return (
      <div className="space-y-4 max-w-5xl" data-testid="ac-report-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setActiveReport(null)} className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground" data-testid="ac-report-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl text-foreground tracking-tight">
              {activeReport.report_type === 'ac_meeting' ? 'AC Agenda-E-Mail' : 'AC Meeting Summary'}
            </h1>
            {activeReport.name && <p className="text-sm text-foreground font-medium mt-0.5">{activeReport.name}</p>}
            <p className="text-sm text-muted-foreground mt-0.5">
              {fmtD(activeReport.created_at)}{activeReport.deal_count != null && ` — ${activeReport.deal_count} Deals`}
            </p>
          </div>
          <Button onClick={copyReport} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-sm h-9 px-4" data-testid="ac-report-copy-btn">
            {copied ? <><Check className="w-4 h-4 mr-2" /> Kopiert</> : <><Copy className="w-4 h-4 mr-2" /> E-Mail kopieren</>}
          </Button>
        </div>
        <div className="bg-white border border-border rounded-sm p-3 sm:p-6 overflow-x-auto">
          <div className="text-sm text-gray-800 leading-relaxed" data-testid="ac-report-body"
            dangerouslySetInnerHTML={{ __html: activeReport.body }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="ac-meeting-page">
      <div>
        <h1 className="font-heading text-2xl text-foreground tracking-tight flex items-center gap-2">
          <Landmark className="w-6 h-6 text-purple-400" /> AC Meeting
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Agenda, Vorab-Mail, Teaser-Vorbereitung, Plaud-Nachbericht und Archiv — alles fürs Advisory Committee an einem Ort.
        </p>
      </div>

      <ACMeetingCard isAdmin={isAdmin} navigate={navigate}
        onAgendaChanged={() => setRefreshKey(k => k + 1)}
        onSummaryArchived={() => { loadHistory(); loadStats(); }} />

      <ACBrokeredPrepCard refreshKey={refreshKey} />

      {passed.length > 0 && (
        <div className="bg-card border border-red-500/25 rounded-sm" data-testid="ac-passed-since-card">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-400" />
            <p className="font-heading text-base text-foreground">Passed seit dem letzten AC-Meeting</p>
            <span className="text-[10px] font-mono text-muted-foreground ml-auto">{passed.length} zuvor besprochene Deals — geht automatisch in die nächste AC-Mail</span>
          </div>
          {passed.map(p => (
            <div key={p.project_id} className="px-4 py-2.5 border-b border-border last:border-b-0" data-testid={`ac-passed-${p.project_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => navigate(`/projects/${p.project_id}`)}
                  className="text-sm font-medium text-foreground hover:text-purple-400 transition-colors">{p.name}</button>
                <span className="text-[10px] text-muted-foreground font-mono">
                  besprochen am {(p.discussed_on || []).slice(-2).map(fmtD).join(', ')} · gepasst am {fmtD(p.passed_at)}{p.passed_by ? ` durch ${p.passed_by}` : ''}
                </span>
              </div>
              {p.reason && <p className="text-xs text-foreground/80 mt-0.5">Grund: {p.reason}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="bg-card border border-border rounded-sm" data-testid="ac-summaries-card">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <p className="font-heading text-base text-foreground">AC Meeting Summaries</p>
          <span className="text-[10px] font-mono text-muted-foreground ml-auto">{summaries.length}</span>
        </div>
        {summaries.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-5">Noch keine AC-Nachberichte — nach dem Meeting Plaud-Notizen hochladen &amp; versenden.</p>
        ) : summaries.map(r => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/10 group"
            data-testid={`ac-summary-row-${r.id}`}>
            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground truncate">{r.name || 'AC Meeting Summary'}</p>
              <p className="text-[10px] text-muted-foreground font-mono">
                {fmtD(r.created_at)}{r.deal_count != null && ` — ${r.deal_count} Deals`}
                {r.sent_to?.length ? ` · an ${r.sent_to.length} Empfänger` : ''}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => viewReport(r.id)} data-testid={`ac-summary-view-${r.id}`}
              className="h-7 text-[11px] text-muted-foreground hover:text-foreground">Ansehen</Button>
            {isAdmin && (
              <button onClick={() => deleteReport(r.id)} className="text-muted-foreground/30 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {agendaEmails.length > 0 && (
          <div className="border-t border-border">
            <button onClick={() => setShowAgendaEmails(v => !v)} data-testid="ac-toggle-agenda-emails"
              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showAgendaEmails ? 'rotate-90' : ''}`} />
              Versendete AC-Agenda-Mails ({agendaEmails.length})
            </button>
            {showAgendaEmails && agendaEmails.map(r => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 border-t border-border/50 hover:bg-secondary/10 group"
                data-testid={`ac-agenda-email-row-${r.id}`}>
                <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground truncate">{r.name || 'AC Agenda-E-Mail'}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">{fmtD(r.created_at)}{r.auto_sent ? ' · auto' : ''}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => viewReport(r.id)}
                  className="h-6 text-[11px] text-muted-foreground hover:text-foreground">Ansehen</Button>
                {isAdmin && (
                  <button onClick={() => deleteReport(r.id)} className="text-muted-foreground/30 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
